import { identity, curryN } from 'ramda';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createSelector } from 'reselect';
import {
  debounce, tryCatchNetworkError, getTexts, getCountries,
} from '@twnel/web-components';
import {
  login, validate, register, registerValidation,
} from './actions';
import { USERID, STEP, LANDSCAPING_INDUSTRY } from './constants';

const getDefaultCountry = createSelector(
  getCountries,
  ({ clientCountry }) => clientCountry.code,
);

export const useResources = () => {
  const texts = useSelector(getTexts);
  const defaultCountry = useSelector(getDefaultCountry);
  return { texts, defaultCountry };
};

const useAction = (action, arity = action.length) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  const onAction = React.useCallback(async (...args) => {
    setLoading(true);
    const [actionError] = await tryCatchNetworkError(action(...args));
    if (actionError) {
      setError(actionError.message);
      setLoading(false);
    }
  }, [action]);
  const onSubmit = React.useMemo(
    () => curryN(arity, loading ? identity : onAction),
    [arity, loading, onAction],
  );
  const clearError = React.useCallback(() => setError(null), []);

  return {
    loading,
    error,
    onSubmit,
    clearError,
  };
};

export const useLogin = (defaultCountry, setStep) => {
  const dispatch = useDispatch();
  const loginAction = React.useCallback(
    async (userId) => {
      const { type } = await dispatch(login(userId, defaultCountry));
      if (type === USERID.USERNAME) {
        setStep(STEP.PASSWORD);
      } else {
        setStep(STEP.VALIDATE);
      }
    },
    [defaultCountry, setStep, dispatch],
  );
  return useAction(loginAction);
};

export const useValidate = (landscaping = false) => {
  const dispatch = useDispatch();
  const defaultCountry = useSelector(getDefaultCountry);
  const validateAction = React.useCallback(
    (userId, credentials) => dispatch(validate({
      userId,
      credentials,
      defaultCountry,
      landscaping,
    })),
    [defaultCountry, dispatch, landscaping],
  );
  return useAction(validateAction, 2);
};

const LOGO_SUGGEST_URL = 'https://autocomplete.clearbit.com/v1/companies/suggest?query=';

const fetchLogo = async (name, signal) => {
  const response = await fetch(`${LOGO_SUGGEST_URL}${encodeURIComponent(name)}`, { signal });
  if (!response.ok) {
    return null;
  }
  const data = await response.json();
  const logo = data?.[0]?.logo;
  if (!logo) {
    return null;
  }
  const imageResponse = await fetch(logo, { signal });
  return imageResponse.ok ? imageResponse.blob() : null;
};

// Best effort: a failed or outdated lookup just leaves the logo empty
let logoLookup = null;
const guessCompanyLogo = debounce(async (name = '', callback) => {
  logoLookup?.abort();
  if (!name) {
    return;
  }
  logoLookup = new AbortController();
  try {
    const blob = await fetchLogo(name, logoLookup.signal);
    if (blob) {
      callback(blob);
    }
  } catch {
    // Network errors and aborted lookups are expected
  }
});

export const useCompanyLogo = (name) => {
  const [logo, setLogo] = React.useState({
    value: null,
    guess: true,
  });

  React.useEffect(() => {
    if (logo.guess) {
      guessCompanyLogo(name, (newLogo) => {
        setLogo((prevLogo) => {
          if (!prevLogo.guess) {
            return prevLogo;
          }
          return {
            value: newLogo,
            guess: true,
          };
        });
      });
    }
  }, [logo.guess, name, setLogo]);

  const setFunc = React.useCallback((newLogo) => {
    setLogo({
      value: newLogo,
      guess: false,
    });
  }, [setLogo]);
  return [logo.value, setFunc];
};

export const useImageURL = (image) => {
  const [url, setUrl] = React.useState(null);
  const prevUrlRef = React.useRef(null);
  React.useEffect(() => {
    if (prevUrlRef.current) URL.revokeObjectURL(prevUrlRef.current);
    const newUrl = image ? URL.createObjectURL(image) : null;
    prevUrlRef.current = newUrl;
    // Object URL creation has no side-effect-free alternative
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setUrl(newUrl);
    return () => {
      if (newUrl) URL.revokeObjectURL(newUrl);
    };
  }, [image]);
  return url;
};

export const useRegisterCompany = (done) => {
  const dispatch = useDispatch();
  const registerAction = React.useCallback(
    async (name, industry, logo) => {
      await dispatch(register({ name, industry, logo }));
      const companyData = { name, industry, logo };
      if (industry === LANDSCAPING_INDUSTRY) {
        companyData.terrachat = { landscaping: true };
      }
      done(companyData);
    },
    [dispatch, done],
  );
  return useAction(registerAction, 3);
};

export const useRegisterValidation = () => {
  const dispatch = useDispatch();
  const defaultCountry = useSelector(getDefaultCountry);
  const registerValidationAction = React.useCallback(
    (...args) => dispatch(registerValidation(...args, defaultCountry)),
    [dispatch, defaultCountry],
  );
  return useAction(registerValidationAction, 2);
};
